import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiPhone, FiMapPin } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmationModal from '../components/confirmation';
import './suppliers.css';

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// Search Input Component (memoized)
const SearchInput = React.memo(
  React.forwardRef(({ value, onChange }, ref) => (
    <div className="search-input-container">
      <FiSearch className="search-icon" />
      <input
        ref={ref}
        type="text"
        placeholder="Search by suppliers"
        value={value}
        onChange={onChange}
        autoComplete="off"
        className="search-input"
      />
    </div>
  ))
);

// SuppliersList component fetches and renders suppliers based on searchTerm and refreshTrigger props
const SuppliersList = React.memo(({ searchTerm, onEdit, onDelete, onNavigate, refreshTrigger }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    axios.get('/suppliers', { params: { search: debouncedSearchTerm } })
      .then(async (response) => {
        if (!isMounted) return;
        // Fetch payments info for each supplier in parallel
        const suppliersWithPayments = await Promise.all(
          response.data.map(async (supplier) => {
            try {
              const paymentsResponse = await axios.get(
                `/suppliers/${supplier.supplier_id}/payments`
              );
              return {
                ...supplier,
                payments: paymentsResponse.data,
                lastTransaction: paymentsResponse.data[0]?.payment_date || null,
              };
            } catch {
              return { ...supplier, payments: [], lastTransaction: null };
            }
          })
        );
        if (isMounted) {
          setSuppliers(suppliersWithPayments);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.response?.data?.error || err.message || 'Failed to fetch suppliers');
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [debouncedSearchTerm, refreshTrigger]); // Added refreshTrigger here

  if (loading) return <div className="loading">Loading suppliers...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (suppliers.length === 0)
    return (
      <div className="empty-state">
        <p>{debouncedSearchTerm ? 'No suppliers match your search criteria' : 'No suppliers found.'}</p>
      </div>
    );

  return (
    <table className="suppliers-table">
      <thead>
        <tr>
          <th>Supplier Name</th>
          <th>Contact Information</th>
          <th>Address</th>
          <th>Due Amount</th>
          <th>Last Transaction</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {suppliers.map((supplier) => {
          const isAdvance = supplier.due_amount < 0;
          const absoluteAmount = Math.abs(supplier.due_amount);

          return (
            <tr key={supplier.supplier_id}>
              <td>
                <div className="supplier-name clickable" onClick={() => onNavigate(supplier.supplier_id)}>
                  {supplier.name}
                  <div className="supplier-email">{supplier.email || 'No email'}</div>
                </div>
              </td>
              <td>
                <div className="contact-info">
                  <FiPhone className="contact-icon" />
                  {supplier.contact_number}
                </div>
              </td>
              <td>
                <div className="address-info">
                  <FiMapPin className="address-icon" />
                  {supplier.address || 'No address'}
                </div>
              </td>
              <td>
                <div className={`due-amount ${isAdvance ? 'advance' : ''}`}>
                  Rs.{absoluteAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {isAdvance && ' (Advance)'}
                </div>
              </td>
              <td>
                {supplier.lastTransaction
                  ? new Date(supplier.lastTransaction).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : 'No transactions'}
              </td>
              <td>
                <div className="action-buttons">
                  <button className="edit-btn" title="Edit" onClick={() => onEdit(supplier.supplier_id)}>
                    <FiEdit2 size={16} />
                  </button>
                  <button className="delete-btn" title="Delete" onClick={() => onDelete(supplier.supplier_id)}>
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
});

const Suppliers = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Added refresh trigger

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleDelete = useCallback((supplierId) => {
    setError(null);
    setSupplierToDelete(supplierId);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await axios.delete(`/suppliers/${supplierToDelete}`);
      setSupplierToDelete(null);
      setShowDeleteModal(false);
      setRefreshTrigger(prev => prev + 1); // Trigger refresh after delete
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete supplier. Please try again.');
      setSupplierToDelete(null);
      setShowDeleteModal(false);
    }
  }, [supplierToDelete]);

  const handleCancelDelete = useCallback(() => {
    setSupplierToDelete(null);
    setShowDeleteModal(false);
  }, []);

  const handleEdit = useCallback(
    (id) => {
      navigate(`/suppliers/edit/${id}`);
    },
    [navigate]
  );

  const handleNavigate = useCallback(
    (id) => {
      navigate(`/suppliers/details/${id}`);
    },
    [navigate]
  );

  return (
    <div className="suppliers-container">
      <div className="suppliers-header">
        <h1 className="suppliers-title">Suppliers</h1>
        <div className="suppliers-controls">
          <SearchInput
            value={searchTerm}
            onChange={handleSearchChange}
            ref={inputRef}
          />
          <button className="add-supplier-btn" onClick={() => navigate('/suppliers/add')}>
            <FiPlus />
            Add Supplier
          </button>
        </div>
      </div>

      <SuppliersList
        searchTerm={searchTerm}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        refreshTrigger={refreshTrigger} // Pass refresh trigger
      />

      <ConfirmationModal
        show={showDeleteModal}
        title="Delete Supplier"
        message="Are you sure you want to delete this supplier? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {error && <div className="error">{error}</div>}
    </div>
  );
};

export default Suppliers;
